import React from "react";
import Dialog, { DialogProps } from "@mui/material/Dialog";

import { useMediaQuery } from "@mui/material";
import theme from "components/theme";
import { DialogTitle } from "./dialog";

type Props = {
  children: React.ReactNode;
  isOpen: boolean;
  onRequestClose(event: React.MouseEvent | React.KeyboardEvent): void;
  title?: React.ReactNode;
  isNotCloseable?: boolean;
  style?: React.CSSProperties;
  fullScreen?: boolean;
  small?: boolean;
  noTitle?: boolean;
}

/** Wraps Material-UI with Haven's styles.
 * See React Modal docs for more info on useage:
 * https://v4.mui.com/api/modal
 *
 * Example with parent state:
 *
 * ```
 * const Page = (props) => {
 *   const [isOpen, setOpenState] = React.useState(true);
 *   return (
 *     <div>
 *       <button onClick={ () => setOpenState(true) }>Open</button
 *
 *       <HavenModal
 *         isOpen={isOpen}
 *         onRequestClose={() => setOpenState(false)}
 *         title="Modal Title"
 *       >
 *         Hello Content
 *       </HavenModal>
 * ```
 */
export const Modal: React.FC<Props & Omit<DialogProps, "open" | "children" | "title">> = (props) => {
  const { isOpen,
    onRequestClose,
    children,
    title,
    small,
    // Default settings to scroll="paper" and fullWidth
    scroll = "paper",
    isNotCloseable,
    ...modalProps } = props;


  const isSmall = useMediaQuery(theme.breakpoints.down("xs"));

  let { fullScreen = false, fullWidth = true } = props;
  if (!isSmall) fullScreen = false;
  if (small) fullWidth = false;

  return (
    <Dialog
      open={isOpen}
      onClose={onRequestClose}
      disableRestoreFocus
      BackdropProps={{ style: { background: "rgba(141, 145, 165, 0.7)" } }}
      {...modalProps}
      scroll={scroll}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
    >
      {!props.noTitle && (
        <DialogTitle onClose={!isNotCloseable && onRequestClose}>{title}</DialogTitle>
      )}
      {children}
    </Dialog>
  );
};
